<!-- Cookie Consent Banner -->
<ng-container *transloco="let t">
  @if (showBanner()) {
    <!-- Backdrop with blur effect -->
    <div class="cookie-banner-backdrop"></div>

    <div
      class="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
    >
      <div class="cookie-banner-content">
        <div class="cookie-banner-text">
          <h2 id="cookie-banner-title" class="cookie-banner-title">
            {{ t('cookieConsentBanner.title') }}
          </h2>
          <p id="cookie-banner-description" class="cookie-banner-description">
            {{ t('cookieConsentBanner.description') }}
            <a
              (click)="goPolicies()"
              (keydown.enter)="goPolicies()"
              class="cookie-link"
              tabindex="0"
              role="button"
              >{{ t('cookieConsentBanner.policyLink') }}</a
            >.
          </p>
        </div>

        <div class="cookie-banner-actions">
          <p-button
            [label]="t('cookieConsentBanner.acceptAll')"
            severity="primary"
            (onClick)="acceptAll()"
            styleClass="cookie-btn cookie-btn-accept"
            [attr.aria-label]="t('cookieConsentBanner.acceptAllAria')"
          />
          <p-button
            [label]="t('cookieConsentBanner.rejectAll')"
            severity="secondary"
            [outlined]="true"
            (onClick)="rejectAll()"
            styleClass="cookie-btn cookie-btn-reject"
            [attr.aria-label]="t('cookieConsentBanner.rejectAllAria')"
          />
          <p-button
            [label]="t('cookieConsentBanner.customize')"
            severity="secondary"
            [outlined]="true"
            (onClick)="openSettings()"
            styleClass="cookie-btn cookie-btn-customize"
            [attr.aria-label]="t('cookieConsentBanner.customizeAria')"
          />
        </div>
      </div>
    </div>
  }

  <!-- Cookie Settings Modal -->
  <p-dialog
    [(visible)]="showSettings"
    [modal]="true"
    [closable]="true"
    [dismissableMask]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="cookie-settings-dialog"
    [header]="t('cookieConsentBanner.settings.header')"
    (onHide)="closeSettings()"
  >
    <div class="cookie-settings-content">
      <p class="cookie-settings-intro">
        {{ t('cookieConsentBanner.settings.intro') }}
      </p>

      <!-- Necessary Cookies (Always enabled) -->
      <div class="cookie-category">
        <div class="cookie-category-header">
          <div class="cookie-category-info">
            <h3 class="cookie-category-title">
              <span class="category-icon">🔐</span>
              {{ t('cookieConsentBanner.settings.necessary.title') }}
            </h3>
            <p class="cookie-category-description">
              {{ t('cookieConsentBanner.settings.necessary.description') }}
            </p>
          </div>
          <p-toggleswitch
            [ngModel]="true"
            [disabled]="true"
            styleClass="cookie-switch"
            [ariaLabel]="t('cookieConsentBanner.settings.necessary.toggleAria')"
          />
        </div>
        <div class="cookie-category-examples">
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.necessary.example1')
          }}</span>
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.necessary.example2')
          }}</span>
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.necessary.example3')
          }}</span>
        </div>
      </div>

      <!-- Analytics Cookies -->
      <div class="cookie-category">
        <div class="cookie-category-header">
          <div class="cookie-category-info">
            <h3 class="cookie-category-title">
              <span class="category-icon">📊</span>
              {{ t('cookieConsentBanner.settings.analytics.title') }}
            </h3>
            <p class="cookie-category-description">
              {{ t('cookieConsentBanner.settings.analytics.description') }}
            </p>
          </div>
          <p-toggleswitch
            [(ngModel)]="preferences().analytics"
            styleClass="cookie-switch"
            [ariaLabel]="t('cookieConsentBanner.settings.analytics.toggleAria')"
          />
        </div>
        <div class="cookie-category-examples">
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.analytics.example1')
          }}</span>
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.analytics.example2')
          }}</span>
        </div>
      </div>

      <!-- Marketing Cookies -->
      <div class="cookie-category">
        <div class="cookie-category-header">
          <div class="cookie-category-info">
            <h3 class="cookie-category-title">
              <span class="category-icon">📢</span>
              {{ t('cookieConsentBanner.settings.marketing.title') }}
            </h3>
            <p class="cookie-category-description">
              {{ t('cookieConsentBanner.settings.marketing.description') }}
            </p>
          </div>
          <p-toggleswitch
            [(ngModel)]="preferences().marketing"
            styleClass="cookie-switch"
            [ariaLabel]="t('cookieConsentBanner.settings.marketing.toggleAria')"
          />
        </div>
        <div class="cookie-category-examples">
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.marketing.example1')
          }}</span>
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.marketing.example2')
          }}</span>
        </div>
      </div>

      <!-- Preference Cookies -->
      <div class="cookie-category">
        <div class="cookie-category-header">
          <div class="cookie-category-info">
            <h3 class="cookie-category-title">
              <span class="category-icon">⚙️</span>
              {{ t('cookieConsentBanner.settings.preferences.title') }}
            </h3>
            <p class="cookie-category-description">
              {{ t('cookieConsentBanner.settings.preferences.description') }}
            </p>
          </div>
          <p-toggleswitch
            [(ngModel)]="preferences().preferences"
            styleClass="cookie-switch"
            [ariaLabel]="t('cookieConsentBanner.settings.preferences.toggleAria')"
          />
        </div>
        <div class="cookie-category-examples">
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.preferences.example1')
          }}</span>
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.preferences.example2')
          }}</span>
          <span class="cookie-example">{{
            t('cookieConsentBanner.settings.preferences.example3')
          }}</span>
        </div>
      </div>

      <div class="cookie-settings-footer">
        <p class="cookie-settings-note">
          {{ t('cookieConsentBanner.settings.footerNote') }}
          <a
            class="cookie-link"
            (click)="closeSettings(); goPolicies()"
            (keydown.enter)="closeSettings(); goPolicies()"
            tabindex="0"
            role="button"
            >{{ t('cookieConsentBanner.policyLink') }}</a
          >.
        </p>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <div class="cookie-settings-actions">
        <p-button
          [label]="t('cookieConsentBanner.settings.save')"
          severity="primary"
          (onClick)="savePreferences()"
          styleClass="cookie-btn-save"
          [attr.aria-label]="t('cookieConsentBanner.settings.saveAria')"
        />
        <p-button
          [label]="t('cookieConsentBanner.settings.cancel')"
          severity="secondary"
          [outlined]="true"
          (onClick)="closeSettings()"
          styleClass="cookie-btn-cancel"
          [attr.aria-label]="t('cookieConsentBanner.settings.cancelAria')"
        />
      </div>
    </ng-template>
  </p-dialog>
</ng-container>
